import React, { useMemo, useState } from "react";
import api from "../../api/axios";
import StickyTableContainer from "../ui/StickyTableContainer";

const number = (value) => Number(value) || 0;
const format = (value) => new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(number(value));

const summaryFields = [
  ["Effectif standard direct total", "direct"],
  ["Coût de revient standard (DH)", "cost"],
  ["Production standard en valeur", "productionValue"],
  ["Minutes consommées", "consumedMinutes"],
  ["Minutes standard productives", "productiveMinutes"],
  ["Rendement", "efficiency", "%"],
  ["Coût minute standard", "minuteCost"],
  ["Marge nette", "netMargin"],
  ["Taux de marge nette", "marginRate", "%"],
];

const GammeProdSummary = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [loadedDate, setLoadedDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadByDate = async (event) => {
    event.preventDefault();
    if (!selectedDate) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/gamme-productions", { params: { date: selectedDate } });
      setRows(response.data.map((snapshot) => {
        const quantity = number(snapshot.quantity);
        const productionValue = number(snapshot.labor_price_per_piece) * quantity;
        const cost = number(snapshot.total_cost);
        const netMargin = productionValue - cost;
        return {
          id: snapshot.id,
          chain: snapshot.chain?.name || `Chaîne ${snapshot.chain_id}`,
          model: snapshot.model?.modele || `Modèle ${snapshot.model_id}`,
          direct: number(snapshot.direct_effective),
          cost,
          productionValue,
          consumedMinutes: number(snapshot.direct_effective) * number(snapshot.worked_hours) * 60,
          productiveMinutes: number(snapshot.productive_minutes),
          efficiency: number(snapshot.efficiency_rate),
          minuteCost: number(snapshot.minute_cost),
          netMargin,
          marginRate: productionValue ? netMargin / productionValue : 0,
        };
      }));
      setLoadedDate(selectedDate);
    } catch (loadError) {
      setRows([]);
      setLoadedDate(selectedDate);
      setError(loadError.response?.data?.message || "Impossible de charger la gamme pour cette date.");
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(() => {
    const summed = rows.reduce((result, row) => ({
      direct: result.direct + row.direct,
      cost: result.cost + row.cost,
      productionValue: result.productionValue + row.productionValue,
      consumedMinutes: result.consumedMinutes + row.consumedMinutes,
      productiveMinutes: result.productiveMinutes + row.productiveMinutes,
      netMargin: result.netMargin + row.netMargin,
    }), { direct: 0, cost: 0, productionValue: 0, consumedMinutes: 0, productiveMinutes: 0, netMargin: 0 });
    return {
      ...summed,
      efficiency: summed.consumedMinutes ? summed.productiveMinutes / summed.consumedMinutes : 0,
      minuteCost: summed.productiveMinutes ? summed.cost / summed.productiveMinutes : 0,
      marginRate: summed.productionValue ? summed.netMargin / summed.productionValue : 0,
    };
  }, [rows]);

  const cells = (row) => <>
    <td className="p-3 border border-gray-300 text-center">{format(row.direct)}</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.cost)}</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.productionValue)}</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.consumedMinutes)}</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.productiveMinutes)}</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.efficiency * 100)}%</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.minuteCost)}</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.netMargin)}</td>
    <td className="p-3 border border-gray-300 text-center">{format(row.marginRate * 100)}%</td>
  </>;

  const mobileCard = (row, consolidated = false) => (
    <article
      key={consolidated ? "consolidated" : row.id}
      className={`overflow-hidden rounded-lg border ${consolidated ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"}`}
    >
      <div className={`px-3 py-2.5 ${consolidated ? "bg-blue-100" : "bg-slate-100"}`}>
        <h3 className="break-words text-sm font-bold text-slate-900">
          {consolidated ? "Atelier consolidé" : row.chain}
        </h3>
        {!consolidated && <p className="break-words text-xs text-slate-600">{row.model}</p>}
      </div>
      <dl className="divide-y divide-slate-200 px-3 text-[11px] leading-4 text-slate-700">
        {summaryFields.map(([label, key, unit]) => {
          const value = unit === "%" ? row[key] * 100 : row[key];
          return (
            <div key={key} className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,38%)] gap-2 py-2">
              <dt className="min-w-0 break-words">{label}</dt>
              <dd className="min-w-0 break-words text-right font-semibold text-slate-950">
                {format(value)}{unit || ""}
              </dd>
            </div>
          );
        })}
      </dl>
    </article>
  );

  return <section className="gamme-summary mt-8 min-w-0 border-t border-gray-200 pt-6 sm:mt-10 sm:pt-8">
    <div className="mb-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <div>
        <h2 className="text-xl font-bold leading-tight text-gray-900 sm:text-2xl">Synthèse de la gamme de production</h2>
        <p className="mt-1 text-sm text-gray-600 sm:text-base">Charge les gammes dont la planification est active à la date sélectionnée.</p>
      </div>
      <form onSubmit={loadByDate} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div>
          <label htmlFor="gamme-summary-date" className="mb-1 block text-sm font-medium text-gray-700">Date de planification</label>
          <input id="gamme-summary-date" type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500" required />
        </div>
        <button type="submit" disabled={loading || !selectedDate} className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">{loading ? "Chargement..." : "Charger"}</button>
      </form>
    </div>

    <div className="mb-5 space-y-1.5 text-xs leading-5 text-gray-700 sm:space-y-2 sm:text-base">
      <p>Coût minute cible : <span className="font-semibold">&lt; 1 DH/min</span></p>
      <p>Coût minute préoccupant : <span className="font-semibold">&gt; 1,5 DH/min</span></p>
      <p>Rendement minimum : <span className="font-semibold">60%</span> — rendement cible : <span className="font-semibold">70%</span> — rendement excellent : <span className="font-semibold">80%</span></p>
      <p>Taux de marge nette cible : <span className="font-semibold">{"> "}20%</span></p>
    </div>

    {error && <p className="rounded-lg bg-red-50 p-4 text-red-700">{error}</p>}
    {!loadedDate && !loading && <p className="rounded-lg bg-gray-50 p-6 text-center text-gray-600">Sélectionnez une date puis cliquez sur Charger.</p>}
    {loadedDate && !loading && !error && rows.length === 0 && <p className="rounded-lg bg-amber-50 p-6 text-center text-amber-800">Aucune gamme enregistrée pour le {loadedDate}.</p>}

    {rows.length > 0 && <>
      <div className="space-y-3 md:hidden">
        {rows.map((row) => mobileCard(row))}
        {mobileCard(totals, true)}
      </div>
      <div className="hidden md:block">
      <StickyTableContainer>
      <table className="dashboard-sticky-table w-full min-w-[1800px] border-collapse bg-slate-200">
        <thead className="bg-gray-100 text-xs uppercase"><tr>
          {["Chaîne", "Modèle", "Effectif standard direct total", "Coût de revient standard (DH)", "Production standard en valeur", "Minutes consommées", "Minutes standard productives", "Rendement", "Coût minute standard", "Marge nette", "Taux de marge nette"].map((label) => <th key={label} className="p-3 border border-gray-300 text-center whitespace-nowrap text-sm md:text-base font-semibold">{label}</th>)}
        </tr></thead>
        <tbody>{rows.map((row) => <tr key={row.id}>
          <td className="p-3 border border-gray-300 font-semibold whitespace-nowrap">{row.chain}</td>
          <td className="p-3 border border-gray-300 whitespace-nowrap">{row.model}</td>
          {cells(row)}
        </tr>)}</tbody>
        <tfoot className="bg-blue-50 font-bold"><tr>
          <td className="p-3 border border-gray-300 whitespace-nowrap">Atelier consolidé</td>
          <td className="p-3 border border-gray-300 text-center">—</td>
          {cells(totals)}
        </tr></tfoot>
      </table>
      </StickyTableContainer>
      </div>
    </>}
  </section>;
};

export default GammeProdSummary;
